import NiceModal, { useModal } from '@ebay/nice-modal-react';

import { useEffect, useMemo, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import SearchInput from 'components/field/SearchInput';
import Authorization from 'components/global/Authorization';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAccessControl from 'hooks/useAccessControl';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocalStorage from 'hooks/useLocalStorage';

import { MONITOR_VIEW } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import PATHS from 'utils/paths';

import { faPuzzle, faSave, faTableColumns, faTrashCan } from '@fortawesome/pro-regular-svg-icons';

import * as S from '../styled/ManageWidgetsModal.styled';
import DraggableWidgetPreview from './DraggableWidgetPreview';
import { ANALYSIS_PAGES, WIDGET_VERSION } from './widgetsUtils';

const WidgetsSettings = ({
	widgetsSet,
	setWidgetsSet,
	defaultWidgetSet,
	handleCheck,
	handlePagesChange,
	pages,
	setLocalPages
}) => {
	const { t } = useTranslation();
	const [searchValue, setSearchValue] = useState(null);
	const [filteredItems, setFilteredItems] = useState(widgetsSet);
	const [selectedPages, setSelectedPages] = useState([]);

	useEffect(() => {
		let newFilteredItems = [...widgetsSet];
		if (searchValue) {
			newFilteredItems = newFilteredItems.filter((w) =>
				t(w.name).toLowerCase().includes(searchValue.toLowerCase())
			);
		}
		if (selectedPages && selectedPages.length > 0) {
			newFilteredItems = newFilteredItems.filter((w) => {
				const widgetPages = w.sourcePage || [];
				return selectedPages.some((page) => widgetPages.includes(page));
			});
		}
		setFilteredItems([...newFilteredItems]);
	}, [searchValue, widgetsSet, selectedPages]);

	return (
		<S.SettingsContainer>
			<div>
				<S.SearchWrapper>
					<div>
						<SearchInput value={searchValue} setValue={setSearchValue} />
					</div>
					<EnumSelect
						data={Object.values(ANALYSIS_PAGES)}
						setValue={setSelectedPages}
						value={selectedPages}
						multipleSelection
						searchable={false}
					/>
				</S.SearchWrapper>
				<div>
					<S.Divider />
					<S.Subtitle>
						<Typography variant="p3" gutterBottom>
							{`${filteredItems.length} ${t('widgetsModal.widgetsFound')}`}
						</Typography>
						<Button
							label={'widgetsModal.reset'}
							variant="blueBorderless"
							disabled={JSON.stringify(widgetsSet) === JSON.stringify(defaultWidgetSet)}
							onClick={() => {
								setWidgetsSet(defaultWidgetSet);
								setFilteredItems(defaultWidgetSet);
							}}
						/>
					</S.Subtitle>
				</div>
			</div>
			<S.List>
				<DndProvider backend={HTML5Backend}>
					{filteredItems.map((widget, index) => (
						<Authorization path={widget.authorizationPath} key={widget.id}>
							<DraggableWidgetPreview
								widget={widget}
								key={widget.id}
								checked={widget.isVisible}
								onCheck={() => handleCheck(widget)}
								onPagesChange={handlePagesChange}
								onWidgetsOrderChange={setWidgetsSet}
								index={index}
								pages={pages}
								setLocalPages={setLocalPages}
							/>
						</Authorization>
					))}
				</DndProvider>
			</S.List>
		</S.SettingsContainer>
	);
};

const PreviewPages = ({ widgetsSet, setWidgetsSet, handleCheck, handlePagesChange, pages }) => {
	const [localPages, setLocalPages] = useState(pages);

	return (
		<div>
			{localPages.map((page) => {
				const widgetsOnPage = widgetsSet.filter((widget) => (widget.pages || []).includes(page));

				return (
					widgetsOnPage.length > 0 && (
						<div key={page}>
							<S.PageHeader>
								<div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
									<Typography variant="p3" fontWeight="700">
										page
									</Typography>
									<Typography variant="p3" fontWeight="700">
										{page}
									</Typography>
								</div>
								<Button
									iconButton
									icon={faTrashCan}
									variant="borderless"
									color="red"
									onClick={() => {
										const updatedPages = localPages.filter((p) => p !== page);
										setLocalPages(updatedPages);
										const updatedWidgets = widgetsSet.map((widget) => ({
											...widget,
											pages: (widget.pages || []).filter((p) => p !== page)
										}));
										setWidgetsSet(updatedWidgets);
									}}
								/>
							</S.PageHeader>
							<S.Divider />
							<S.PreviewPagesWidgetsList>
								{widgetsOnPage.map((widget, index) => (
									<DndProvider backend={HTML5Backend} key={widget.id}>
										<Authorization path={widget.authorizationPath}>
											<DraggableWidgetPreview
												widget={widget}
												checked={widget.isVisible}
												onCheck={() => handleCheck(widget)}
												onPagesChange={handlePagesChange}
												onWidgetsOrderChange={setWidgetsSet}
												index={index}
												pages={pages}
											/>
										</Authorization>
									</DndProvider>
								))}
							</S.PreviewPagesWidgetsList>
						</div>
					)
				);
			})}
		</div>
	);
};

const ManageWidgetsModal = NiceModal.create(({ data, pages, monitorViewData }) => {
	const modal = useModal();
	const { pathname } = useLocation();
	const isMonitorView = pathname.includes('monitor-view');
	const defaultWidgetSet = data[pathname];
	const axios = useAxiosPrivate();
	const [localPages, setLocalPages] = useState(pages || []);

	const [widgetsSetStore, setWidgetsSetStore] = useLocalStorage(`${pathname}${WIDGET_VERSION}`, defaultWidgetSet);
	const [widgetsSet, setWidgetsSet] = useState(isMonitorView ? monitorViewData || defaultWidgetSet : widgetsSetStore);

	const isAuthorized = useAccessControl();

	useEffect(() => {
		setWidgetsSet((prevWidgetsSet) => prevWidgetsSet.filter((widget) => isAuthorized(widget.authorizationPath)));
	}, []);

	const handleSave = async () => {
		try {
			if (pathname.startsWith(PATHS.DASHBOARD_MONITOR_VIEW) || pathname.startsWith(PATHS.ANALYSIS_MONITOR_VIEW)) {
				await axios.post(MONITOR_VIEW.UPDATE_VIEW, {
					widgets: widgetsSet,
					view: pathname
				});
			} else {
				if (JSON.stringify(widgetsSet) !== JSON.stringify(defaultWidgetSet)) {
					setWidgetsSetStore(widgetsSet);
				} else {
					localStorage.removeItem(`${pathname}${WIDGET_VERSION}`);
				}
			}
			window.dispatchEvent(new Event('updateWidgetStore'));
			window.dispatchEvent(new Event('updateWidgetVisibility'));
			modal.remove();
		} catch (error) {
			AppLogger.error('Failed to save widget settings', error);
		}
	};

	const handleCheck = (widget) => {
		setWidgetsSet((prevWidgetsSet) =>
			prevWidgetsSet.map((widgetSetItem) =>
				widgetSetItem.id === widget.id
					? { ...widgetSetItem, isVisible: !widgetSetItem.isVisible }
					: widgetSetItem
			)
		);
	};

	const handlePagesChange = (widgetId, pages) => {
		setWidgetsSet((prevWidgetsSet) =>
			prevWidgetsSet.map((widgetSetItem) =>
				widgetSetItem.id === widgetId ? { ...widgetSetItem, pages } : widgetSetItem
			)
		);
	};

	const widgetSettings = useMemo(
		() => (
			<WidgetsSettings
				widgetsSet={widgetsSet}
				setWidgetsSet={setWidgetsSet}
				defaultWidgetSet={defaultWidgetSet}
				handleCheck={handleCheck}
				handlePagesChange={handlePagesChange}
				pages={localPages || []}
				setLocalPages={setLocalPages}
			/>
		),
		[widgetsSet, setWidgetsSet, defaultWidgetSet, localPages, setLocalPages]
	);

	const [openedTab, setOpenedTab] = useState(0);

	return (
		<Modal position="right" fullwidth maxWidth="x-small" closeOnClickOutside>
			<ModalTitle>
				<Typography variant="p1">{'widgetsModal.title'}</Typography>
			</ModalTitle>
			<ModalContent style={{ height: '100%' }}>
				<div>
					{isMonitorView ? (
						<>
							<S.ModalMenu>
								<Button
									variant={openedTab === 0 ? 'outline' : 'borderless'}
									icon={faPuzzle}
									fullWidth
									onClick={() => setOpenedTab(0)}
									label="widgets"
								/>
								<Button
									variant={openedTab === 1 ? 'outline' : 'borderless'}
									icon={faTableColumns}
									fullWidth
									onClick={() => setOpenedTab(1)}
									label="previewPages"
								/>
							</S.ModalMenu>
							{openedTab === 0 ? (
								widgetSettings
							) : (
								<PreviewPages
									widgetsSet={widgetsSet}
									setWidgetsSet={setWidgetsSet}
									handleCheck={handleCheck}
									handlePagesChange={handlePagesChange}
									pages={localPages || []}
								/>
							)}
						</>
					) : (
						widgetSettings
					)}
				</div>
			</ModalContent>
			<ModalActions>
				<Button label={'cancel'} variant="outline" onClick={() => modal.remove()} />
				<Button
					disabled={
						isMonitorView
							? JSON.stringify(defaultWidgetSet) === JSON.stringify(monitorViewData)
							: JSON.stringify(widgetsSet) === JSON.stringify(widgetsSetStore)
					}
					icon={faSave}
					label={'save'}
					variant="blue"
					onClick={handleSave}
				/>
			</ModalActions>
		</Modal>
	);
});

export default ManageWidgetsModal;
